// src/components/SearchBar.tsx
import { useState, FormEvent, forwardRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface SearchBarProps {
    onClose?: () => void; // Optional: Callback to close the search bar
}

// Use forwardRef to allow parent component to pass a ref to the input element
const SearchBar = forwardRef<HTMLInputElement, SearchBarProps>(({ onClose }, ref) => {
    const [query, setQuery] = useState('');
    const navigate = useNavigate();

    const handleSearch = (e: FormEvent) => {
        e.preventDefault();
        const trimmedQuery = query.trim();
        if (trimmedQuery) {
            navigate(`/search?q=${encodeURIComponent(trimmedQuery)}`);
            setQuery(''); // Clear query after navigation
            onClose?.(); // Close the search bar if callback provided
        }
    };

    // Handle Escape key to close
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose?.();
            }
        };
        // Add listener only when onClose is provided
        if (onClose) {
            document.addEventListener('keydown', handleKeyDown);
            return () => {
                document.removeEventListener('keydown', handleKeyDown);
            };
        }
    }, [onClose]);

    return (
        // Adjust width and styling for header integration
        <form onSubmit={handleSearch} className="relative w-full max-w-xs sm:max-w-sm md:max-w-md animate-fade-in">
            {/* Input field */}
            <input
                ref={ref} // Attach the forwarded ref here
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search posts or subreddits..."
                className="w-full pl-9 pr-8 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 shadow-sm"
                aria-label="Search Reddit"
            />
            {/* Search Icon Button inside input */}
            <button
                type="submit"
                className="absolute left-0 top-0 h-full px-2.5 text-gray-400 dark:text-gray-500 hover:text-indigo-600 dark:hover:text-indigo-400 focus:outline-none"
                aria-label="Submit Search"
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
            </button>
            {/* Close Button (only shown if onClose is provided) */}
             {onClose && (
                 <button
                     type="button"
                     onClick={onClose}
                     className="absolute right-0 top-0 h-full px-2.5 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 focus:outline-none"
                     aria-label="Close Search"
                 >
                     <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                         <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                     </svg>
                 </button>
             )}
        </form>
    );
});

SearchBar.displayName = 'SearchBar'; // Add display name for DevTools

export default SearchBar;